import React from 'react';

import { Typography, useTheme } from '@material-ui/core';

import { getColor } from '@vyce/core/src/theme/styles';
import { VAT_TREATMENTS } from '@vyce/core/src/types';
import { currencyFormatter, formatPercent } from '@vyce/core/src/utils';

/** Shown instead of an empty cell so a VAT-less line doesn't read as a rendering bug. */
export const EMPTY_VALUE = '—';

interface VatCellProps {
  vat?: number;
  /** A decimal string from the API, or an amount the caller has already summed. */
  drcVat?: string | number;
  treatment?: VAT_TREATMENTS;
  vatRate?: string | null;
}

/**
 * The VAT column shows what was actually paid to the worker. On a reverse-charged line that is
 * zero by definition, shown greyed rather than blank so it reads as "nothing was paid" rather
 * than "no data".
 *
 * Both cells branch on `vat_treatment`, never on whether an amount happens to be zero: the
 * treatment is frozen when the run is submitted, and an unpaid standard-rated line is also £0.00.
 */
export const VatAmountCell: React.FC<VatCellProps> = ({ vat, treatment }) => {
  const theme = useTheme();

  if (treatment === VAT_TREATMENTS.NONE) {
    return <span>{EMPTY_VALUE}</span>;
  }

  if (treatment === VAT_TREATMENTS.REVERSE_CHARGE) {
    return (
      <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
        {currencyFormatter.format(0)}
      </Typography>
    );
  }

  // No treatment on the row means a pay run calculated before DRC shipped — show VAT as before.
  return <span>{currencyFormatter.format(vat || 0)}</span>;
};

/** Marks a pay-run row whose VAT was withheld rather than paid. */
export const ReverseChargePill: React.FC = () => {
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');

  return (
    <Typography
      component="span"
      variant="caption"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 7px',
        borderRadius: 999,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: colors.pillInfoBG,
        color: colors.pillInfoText,
      }}>
      Reverse charge
    </Typography>
  );
};

export const DRCVatCell: React.FC<VatCellProps> = ({ drcVat, treatment, vatRate }) => {
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');

  if (treatment !== VAT_TREATMENTS.REVERSE_CHARGE) {
    return <span>{EMPTY_VALUE}</span>;
  }

  const rate = formatPercent(vatRate);

  return (
    <Typography
      component="span"
      variant="body2"
      title={rate ? `Reverse charge VAT at ${rate}` : 'Reverse charge VAT'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 8,
        fontWeight: 600,
        background: colors.pillInfoBG,
        color: colors.pillInfoText,
      }}>
      {currencyFormatter.format(Number(drcVat || 0))}
    </Typography>
  );
};
